import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, ListChecks, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import TaskItem from '@/components/tasks/TaskItem';
import TaskForm from '@/components/tasks/TaskForm';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';

const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const { toast } = useToast();

  const fetchTasks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('tasks')
        .select(`
          id,
          name,
          description,
          due_date,
          created_at,
          updated_at,
          project_id,
          projects (id, name),
          assignee_id,
          employees (id, name),
          status_id,
          custom_statuses (id, name)
        `)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setTasks(data || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again.');
      toast({
        title: 'Error',
        description: 'Could not fetch tasks.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleOpenForm = (task = null) => {
    setSelectedTask(task);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setSelectedTask(null);
    setIsFormOpen(false);
  };

  const handleSaveTask = async (taskData) => {
    try {
      let response;
      // Ensure IDs are null if not set, not empty strings
      const dataToSave = {
        name: taskData.name,
        description: taskData.description || null,
        project_id: taskData.project_id || null,
        assignee_id: taskData.assignee_id || null,
        status_id: taskData.status_id || null,
        due_date: taskData.due_date || null,
      };

      if (selectedTask && selectedTask.id) {
        response = await supabase
          .from('tasks')
          .update(dataToSave)
          .eq('id', selectedTask.id)
          .select()
          .single();
      } else {
        response = await supabase
          .from('tasks')
          .insert(dataToSave)
          .select()
          .single();
      }

      const { data, error: saveError } = response;

      if (saveError) throw saveError;

      toast({
        title: 'Success!',
        description: `Task ${selectedTask ? 'updated' : 'created'} successfully.`,
        className: 'bg-green-600 text-white',
      });
      fetchTasks(); 
      handleCloseForm();
    } catch (err) {
      console.error('Error saving task:', err);
      toast({
        title: 'Error saving task',
        description: err.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);

      if (deleteError) throw deleteError;

      toast({
        title: 'Task Deleted',
        description: 'The task has been successfully deleted.',
      });
      fetchTasks(); 
    } catch (err) {
      console.error('Error deleting task:', err);
      toast({
        title: 'Error deleting task',
        description: err.message || 'Could not delete the task.',
        variant: 'destructive',
      });
    }
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="space-y-8">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center"
      >
        <h1 className="text-3xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-500">
          Tasks
        </h1>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenForm()} className="bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white">
              <PlusCircle size={20} className="mr-2" /> Add New Task
            </Button>
          </DialogTrigger>
          {isFormOpen && (
            <TaskForm
              task={selectedTask}
              onSave={handleSaveTask}
              onCancel={handleCloseForm}
            />
          )}
        </Dialog>
      </motion.div>

      {isLoading && (
        <div className="flex justify-center items-center h-[calc(100vh-300px)]">
          <Loader2 className="h-12 w-12 animate-spin text-blue-400" />
          <p className="ml-4 text-xl text-slate-300">Loading tasks...</p>
        </div>
      )}

      {!isLoading && error && (
        <div className="flex flex-col items-center justify-center h-[calc(100vh-300px)] text-red-400 bg-red-500/10 p-6 rounded-lg">
          <AlertTriangle size={48} className="mb-4" />
          <p className="text-xl font-semibold">{error}</p>
        </div>
      )}

      {!isLoading && !error && tasks.length === 0 && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center h-[calc(100vh-300px)] text-slate-500"
        >
          <ListChecks className="w-24 h-24 mb-6 text-green-500/30" />
          <h2 className="text-2xl font-semibold mb-2 text-slate-300">No Tasks Yet</h2>
          <p className="text-slate-400">Click "Add New Task" to get started.</p>
        </motion.div>
      )}

      {!isLoading && !error && tasks.length > 0 && (
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <AnimatePresence>
            {tasks.map((task) => (
              <TaskItem 
                key={task.id} 
                task={task} 
                onEdit={() => handleOpenForm(task)}
                onDelete={handleDeleteTask}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
};

export default TasksPage;